/**
 * Hero Animations using GSAP ScrollTrigger & GSAP SplitText
 */

export class HeroAnimations {
  constructor() {
    this.init();
  }

  /**
   * Initialize animations
   */
  init() {
    if (
      typeof gsap === "undefined" ||
      typeof ScrollTrigger === "undefined" ||
      typeof SplitText === "undefined"
    ) {
      console.warn("GSAP, ScrollTrigger, or SplitText not loaded");
      return;
    }

    gsap.registerPlugin(ScrollTrigger, SplitText);
    this.mm = gsap.matchMedia();

    this.homeHeroAnimations();
    this.platformHeroAnimations();
    this.commonHeroAnimations();
  }

  /**
   * Home Hero Animations on load
   */
  homeHeroAnimations() {
    const section = document.querySelector(".wps-header-home-hero");
    if (!section) return;

    function intro() {
      const container = section.querySelector(
        ".wps-header-home-hero__container",
      );

      const smallTitle = section.querySelector("[data-small-title]");
      const mainTitle = section.querySelector("[data-main-title]");
      const description = section.querySelector("[data-description]");
      const ctas = section.querySelectorAll(".ctas a");
      const topImage = section.querySelector(".top-image__container");
      const floatingImgs = section.querySelectorAll(".floating__images img");
      const mainImage = section.querySelector(".main__image img");

      gsap.set(container, {
        autoAlpha: 1,
      });

      gsap.set([smallTitle, mainTitle, description, ctas], {
        opacity: 0,
        y: 60,
      });

      gsap.set(topImage, {
        opacity: 0,
        scale: 0.8,
      });

      const tl = gsap.timeline();

      tl.to(
        smallTitle,
        {
          opacity: 1,
          y: 0,
          duration: 0.6,
        },
        "-=0.6",
      );

      tl.to(
        mainTitle,
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
        },
        "-=0.4",
      );

      tl.to(
        description,
        {
          opacity: 1,
          y: 0,
          duration: 0.6,
        },
        "-=0.5",
      );

      tl.to(
        ctas,
        {
          opacity: 1,
          y: 0,
          stagger: 0.15,
          duration: 0.5,
        },
        "-=0.4",
      );

      tl.to(
        topImage,
        {
          opacity: 1,
          scale: 1,
          duration: 1,
          ease: "power3.out",
        },
        "-=0.8",
      );

      tl.from(
        floatingImgs,
        {
          opacity: 0,
          scale: 0.6,
          duration: 0.75,
          ease: "back.out(1.2)",
          stagger: {
            each: 0.2,
            from: "random",
          },
        },
        "-=0.8",
      );
    }

    function rollingTexts() {
      const texts = section.querySelectorAll(".rolling-texts .text");
      if (!texts.length) return;

      gsap.set(texts, {
        yPercent: 100,
        opacity: 0,
      });

      const tl = gsap.timeline({ repeat: -1 });

      const ENTER_DURATION = 0.5;
      const HOLD_DURATION = 0.75;
      const EXIT_DURATION = 0.5;

      texts.forEach((text, _) => {
        tl.to(text, {
          yPercent: 0,
          opacity: 1,
          duration: ENTER_DURATION,
          ease: "power3.out",
        });

        tl.to({}, { duration: HOLD_DURATION });

        tl.to(text, {
          yPercent: -100,
          opacity: 0,
          duration: EXIT_DURATION,
          ease: "power3.in",
        });
      });
    }

    intro();
    rollingTexts();
  }

  /**
   * Platform Hero Animations on load
   */
  platformHeroAnimations() {
    const section = document.querySelector("[data-platform-hero]");
    if (!section) return;

    function intro() {
      const container = section.querySelector("[data-platform-hero-container]");

      const badge = section.querySelector("[data-small-title]");
      const title = section.querySelector("[data-main-title]");
      const description = section.querySelector("[data-description]");
      const ctas = section.querySelectorAll("[data-cta]");
      const stats = section.querySelectorAll("[data-stat]");

      const imageContainer = section.querySelector("[data-image]");
      const floatingImgs = section.querySelectorAll(".floating__images img");
      const image = imageContainer.querySelector("img");

      gsap.set(image, {
        opacity: 0,
        scale: 0.8,
      });

      gsap.set(container, {
        autoAlpha: 1,
      });

      if (stats.length) {
        stats.forEach((stat) => {
          gsap.set(stat, {
            y: 20,
            opacity: 0,
          });
        });
      }

      ctas.forEach((cta) => {
        gsap.set(cta, {
          y: 20,
          opacity: 0,
        });
      });

      gsap.set([badge, description], {
        y: 20,
        opacity: 0,
      });

      gsap.set(title, {
        y: 60,
        opacity: 0,
      });

      gsap.set(image, {
        scale: 1.2,
      });

      const targets = [...Array.from(ctas || []), ...Array.from(stats || [])];

      const tl = gsap.timeline({
        defaults: { ease: "power3.out" },
      });

      tl.to(badge, {
        y: 0,
        opacity: 1,
        duration: 0.4,
      })

        .to(
          title,
          {
            y: 0,
            opacity: 1,
            duration: 0.9,
          },
          "-=0.1",
        )

        .to(
          description,
          {
            y: 0,
            opacity: 1,
            duration: 0.6,
          },
          "-=0.5",
        )

        .to(
          targets,
          {
            y: 0,
            opacity: 1,
            duration: 0.5,
            stagger: 0.25,
          },
          "-=0.4",
        );

      tl.to(
        image,
        {
          opacity: 1,
          scale: 1,
          duration: 1,
          ease: "power3.out",
        },
        "-=0.8",
      );
      tl.from(
        floatingImgs,
        {
          opacity: 0,
          scale: 0.6,
          duration: 0.75,
          ease: "back.out(1.2)",
          stagger: {
            each: 0.2,
            from: "random",
          },
        },
        "-=0.8",
      );
    }

    intro();
  }

  /**
   * Common Hero Animations on load
   */
  commonHeroAnimations() {
    function heroOverlay() {
      const section = document.querySelector(".hero__overlay");
      if (!section) return;

      const marquee = document.querySelector("[data-client-logos-marquee]");
      const overlayContainer = section.querySelector(".overlay");

      let WIDTH_TO_COVER;

      function calcWidth() {
        WIDTH_TO_COVER = marquee.getBoundingClientRect().left;
      }
      calcWidth();

      window.addEventListener("resize", calcWidth);

      gsap.set(overlayContainer, {
        width: `${WIDTH_TO_COVER}px`,
      });
    }

    function clientsLogoMarquee() {
      const section = document.querySelector("[data-client-logos-marquee]");
      if (!section) return;

      const containers = section.querySelectorAll(".client-logos__container");

      const width = containers[0].scrollWidth;

      const speed = 100;
      const duration = width / speed;

      gsap.to(containers, {
        x: `-=${width}`,
        duration: duration,
        ease: "none",
        repeat: -1,
        modifiers: {
          x: gsap.utils.unitize((x) => parseFloat(x) % width),
        },
      });
    }

    heroOverlay();
    clientsLogoMarquee();
  }
}
